import React, { useEffect, useMemo, useState } from 'react';
import {
  A_FLOW_STATUSES,
  B_FLOW_STATUSES,
  CATEGORY_CODES,
  KNOWN_STORE_CODES,
  Severity,
  TicketStatus,
  statusLabel
} from '@/types/domain';
import type { ListTicketsQuery } from '@/types/api';
import { useStores } from '@/hooks/useStores';
import { MultiSelect, MultiSelectOption } from '@/components/ui/MultiSelect';
import { DateField } from '@/components/ui/DateField';
import {
  CATEGORY_NAMES,
  SEVERITY_NAMES,
  STORE_NAMES
} from '@/lib/labels';

interface Props {
  value: ListTicketsQuery;
  onChange: (next: ListTicketsQuery) => void;
  /** V4.9.25 — hide the Category control (used inside a category-group tab,
   *  where the category is fixed by the tab so it can't be changed away). */
  hideCategory?: boolean;
}

export function TicketFiltersBar({ value, onChange, hideCategory }: Props) {
  const storesQuery = useStores();
  const storeCodes = (storesQuery.data ?? []).map((store) => store.code);
  const storeOptions = storeCodes.length > 0 ? storeCodes : [...KNOWN_STORE_CODES];

  const update = (patch: Partial<ListTicketsQuery>) => {
    onChange({ ...value, page: 1, ...patch });
  };

  // V4.9.17 — "Custom range" sticky mode. Selecting "Custom range…" used to call
  // computeDateRangeFromPreset('custom') which returned {undefined, undefined},
  // clearing both dates — so getDatePreset() then read back "any", the dropdown
  // snapped to "Any time", and the date pickers never appeared. Result: custom
  // range was impossible to use. We now track an explicit `customOpen` flag so
  // the dropdown stays on Custom (and shows the pickers) even before the user
  // has entered any dates.
  const [customOpen, setCustomOpen] = useState(false);
  const datePreset = getDatePreset(value.created_after, value.created_before);
  const showCustom = customOpen || datePreset === 'custom';

  // V4.9.19 — Debounced search. The input updates a local draft instantly (so
  // typing stays snappy) and the actual query fires 300ms after the user stops
  // typing — far fewer requests, lightning-fast with the new trigram indexes.
  const [searchDraft, setSearchDraft] = useState(value.search ?? '');
  useEffect(() => {
    setSearchDraft(value.search ?? '');
  }, [value.search]);
  useEffect(() => {
    const current = value.search ?? '';
    if (searchDraft === current) return;
    const handle = setTimeout(() => {
      onChange({ ...value, page: 1, search: searchDraft.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);


  const setMulti = <K extends keyof ListTicketsQuery>(key: K) => (next: string[]) => {
    update({ [key]: (next.length ? next : undefined) as ListTicketsQuery[K] });
  };

  // V4.9.8 — SMART STATUS FILTER.
  //
  // Status options auto-narrow based on the Category filter selection:
  //   - No categories selected         → show A-flow + B-flow + Both sections
  //   - Only A categories selected     → show A-flow + Both (hide B-flow)
  //   - Only B categories selected     → show B-flow + Both (hide A-flow)
  //   - Mixed A + B                    → show all three sections
  //
  // Grouped with section headers (A-flow / B-flow / Both flows) so admins
  // can scan options without confusion about which flow a status belongs to.
  const statusOptions = useMemo<MultiSelectOption[]>(() => {
    const selectedCats = (value.category as string[]) ?? [];
    const hasA = selectedCats.length === 0 || selectedCats.some((c) => c.toUpperCase().startsWith('A'));
    const hasB = selectedCats.length === 0 || selectedCats.some((c) => c.toUpperCase().startsWith('B'));

    // Shared statuses appear in both flow lists. We list them ONCE under
    // "Both flows" to avoid duplicate checkboxes.
    const SHARED = new Set<TicketStatus>([TicketStatus.Open, TicketStatus.Cancelled]);

    const aOnly = A_FLOW_STATUSES.filter((s) => !SHARED.has(s));
    const bOnly = B_FLOW_STATUSES.filter((s) => !SHARED.has(s));

    const out: MultiSelectOption[] = [];
    if (hasA) {
      for (const s of aOnly) {
        out.push({
          value: s,
          label: statusLabel(s),
          group: 'A-flow · Paid order incidents (A1–A7)'
        });
      }
    }
    if (hasB) {
      for (const s of bOnly) {
        out.push({
          value: s,
          label: statusLabel(s),
          group: 'B-flow · Sales / inquiries (B1–B4)'
        });
      }
    }
    // "Both flows" section — always shown (every flow needs Open + Cancelled).
    for (const s of SHARED) {
      out.push({ value: s, label: s, group: 'Both flows' });
    }
    return out;
  }, [value.category]);

  return (
    <div className="filter-bar">
      <div className="form-group filter-bar-search" style={{ margin: 0 }}>
        <label className="form-label">Search</label>
        <input
          className="form-input filter-search-input"
          placeholder="Search ticket #, customer, order #, tracking, summary or person…"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
        />
      </div>

      {/* V4.9.8 — Status filter now grouped by flow with section headers.
          Auto-narrows based on the Category filter so you only see options
          that are relevant to your current selection. */}
      <MultiSelect
        label="Status"
        options={statusOptions}
        value={(value.status as string[]) ?? []}
        onChange={setMulti('status')}
      />

      {!hideCategory ? (
        <MultiSelect
          label="Category"
          options={CATEGORY_CODES.map((code) => ({
            value: code,
            label: `${code} — ${CATEGORY_NAMES[code] ?? code}`
          }))}
          value={(value.category as string[]) ?? []}
          onChange={setMulti('category')}
        />
      ) : null}

      <MultiSelect
        label="Store"
        options={storeOptions.map((code) => ({
          value: code,
          label: STORE_NAMES[code] ? `${code} — ${STORE_NAMES[code]}` : code
        }))}
        value={(value.store as string[]) ?? []}
        onChange={setMulti('store')}
      />

      <MultiSelect
        label="Severity"
        options={Object.values(Severity).map((sev) => ({
          value: sev,
          label: `${sev} — ${SEVERITY_NAMES[sev] ?? sev}`
        }))}
        value={(value.severity as string[]) ?? []}
        onChange={setMulti('severity')}
      />

      {/* V4.9.10 — Date filter (created date). Presets + custom range.
         The DTO accepts ISO strings for created_after / created_before. */}
      <div className="form-group" style={{ margin: 0 }}>
        <label className="form-label">Created</label>
        <select
          className="form-select"
          value={showCustom ? 'custom' : datePreset}
          onChange={(e) => {
            const preset = e.target.value;
            if (preset === 'custom') {
              // Open the custom pickers and STAY on "Custom range" — keep any
              // dates already chosen instead of clearing them (which is what
              // made the dropdown snap back to "Any time" before).
              setCustomOpen(true);
              return;
            }
            setCustomOpen(false);
            const range = computeDateRangeFromPreset(preset);
            update({
              created_after: range.after,
              created_before: range.before
            });
          }}
          title="Filter tickets by when they were created. Pick a preset or 'Custom range' to choose exact dates."
        >
          <option value="any">Any time</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="last_7">Last 7 days</option>
          <option value="last_30">Last 30 days</option>
          <option value="this_month">This month</option>
          <option value="last_month">Last month</option>
          <option value="custom">Custom range…</option>
        </select>
        {showCustom ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            <DateField
              label="From"
              ariaLabel="Created from date"
              value={(value.created_after ?? '').slice(0, 10)}
              onChange={(ymd) =>
                update({ created_after: ymd ? `${ymd}T00:00:00.000Z` : undefined })
              }
            />
            <DateField
              label="To"
              ariaLabel="Created to date"
              value={(value.created_before ?? '').slice(0, 10)}
              onChange={(ymd) =>
                update({ created_before: ymd ? `${ymd}T23:59:59.999Z` : undefined })
              }
            />
          </div>
        ) : null}
      </div>

      <div className="form-group" style={{ margin: 0 }}>
        <label className="form-label">Sort</label>
        <select
          className="form-select"
          value={value.sort ?? 'smart'}
          onChange={(e) => update({ sort: e.target.value })}
          title="Smart sort groups tickets by urgency: BREACHED first, then In-progress, Open, Waiting states, then Resolved/Cancelled at the bottom. Within each group: P1 first, then due-soonest, then newest. 'Action awaited by — grouped' groups tickets by the SAME person currently holding them up (Janani's together, Leela's together, Shyam's together, etc.), with Admin approvals + external waits + done tickets pinned to the bottom."
        >
          <option value="smart">Smart (recommended)</option>
          <option value="awaited_by">Action awaited by — grouped (Admin last)</option>
          <option value="created_at.desc">Newest first</option>
          <option value="created_at.asc">Oldest first</option>
          <option value="sla_deadline.asc">Due — soonest</option>
          <option value="severity.asc">Severity — P1 first</option>
          <option value="status.asc">Status — urgent first</option>
        </select>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// V4.9.10 — Date preset helpers for the "Created" filter.
// ────────────────────────────────────────────────────────────────────

/** The presets whose ranges are derived from "now", in match-priority order. */
const DATE_PRESETS = ['today', 'yesterday', 'last_7', 'last_30', 'this_month', 'last_month'] as const;

/** Local calendar day (YYYY-MM-DD) of an ISO timestamp — timezone-correct. */
function localDay(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

/**
 * Inspect the current after/before pair and return the matching preset name.
 * Used by the dropdown's `value=` so the UI reflects the current filter state.
 *
 * V4.9.14 BUGFIX (timezone) — the old version built preset boundaries in LOCAL
 * time (computeDateRangeFromPreset uses local midnight) but compared them as
 * UTC date strings (`toISOString().slice(0,10)`). In any timezone behind/ahead
 * of UTC (e.g. IST), the UTC date is a different calendar day, so the match
 * ALWAYS failed and every preset — Today, Yesterday, Last 7/30 days, This/Last
 * month — read back as "Custom range", snapping the dropdown to Custom on
 * selection. We now compare the stored range against each preset's freshly
 * computed range using LOCAL calendar days on BOTH sides, so the round-trip is
 * stable in every timezone and each preset sticks + filters correctly.
 */
function getDatePreset(after?: string, before?: string): string {
  if (!after && !before) return 'any';
  const afterDay = localDay(after);
  const beforeDay = localDay(before);
  for (const preset of DATE_PRESETS) {
    const r = computeDateRangeFromPreset(preset);
    if (localDay(r.after) === afterDay && localDay(r.before) === beforeDay) {
      return preset;
    }
  }
  return 'custom';
}

/**
 * Given a preset name, compute the ISO timestamps for after / before.
 * 'any' clears both. 'custom' returns both as null so the date pickers
 * show empty until the user picks something.
 */
function computeDateRangeFromPreset(preset: string): { after?: string; before?: string } {
  const now = new Date();
  const isoStartOf = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0).toISOString();
  const isoEndOf = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();

  switch (preset) {
    case 'any':
      return { after: undefined, before: undefined };
    case 'today':
      return { after: isoStartOf(now), before: isoEndOf(now) };
    case 'yesterday': {
      const y = new Date(now);
      y.setDate(now.getDate() - 1);
      return { after: isoStartOf(y), before: isoEndOf(y) };
    }
    case 'last_7': {
      const d7 = new Date(now);
      d7.setDate(now.getDate() - 7);
      return { after: isoStartOf(d7), before: isoEndOf(now) };
    }
    case 'last_30': {
      const d30 = new Date(now);
      d30.setDate(now.getDate() - 30);
      return { after: isoStartOf(d30), before: isoEndOf(now) };
    }
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { after: isoStartOf(start), before: isoEndOf(now) };
    }
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { after: isoStartOf(start), before: isoEndOf(end) };
    }
    case 'custom':
      // Keep whatever is in the form (or empty if first selection).
      return { after: undefined, before: undefined };
    default:
      return { after: undefined, before: undefined };
  }
}
